import React, { useRef, useState } from "react"
import Dialog from "@mui/material/Dialog"
import Button from "@mui/material/Button"
import "../../../styles/components/enquiry-form.scss"
import PhoneUtil, { PhoneNumberFormat as PNF } from "google-libphonenumber"
import CheckForUTMvariables from "@/scripts/modules/utm-fields"

function PhoneInput({ name, required }) {
  const phoneRef = useRef()
  const phoneUtil = PhoneUtil.PhoneNumberUtil.getInstance()
  const country = window.Shopify.country

  const handlePhoneInput = (event) => {
    const rawValue = event.target.value

    try {
      const parsedNumber = phoneUtil.parse(rawValue, country)
      phoneRef.current.value = phoneUtil.format(parsedNumber, PNF.E164)
    } catch (e) {
      phoneRef.current.value = ``
    }
  }

  return (
    <>
      <input
        name={`${name}-mask`}
        required={required}
        type="tel"
        id={`${name}-mask`}
        onChange={handlePhoneInput}
      />
      <input
        name={`${name}`}
        required={required}
        type="hidden"
        id={name}
        ref={phoneRef}
      />
    </>
  )
}

function SelectInput({ options, ...restProps }) {
  return (
    <select {...restProps}>
      {options.map((option) => {
        if (typeof option === `string`) {
          return (
            <option key={option} value={option}>
              {option}
            </option>
          )
        } else {
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          )
        }
      })}
    </select>
  )
}

function Input(props) {
  const { label, children, ...dataProps } = props.data
  const type = props.data.type || `text`

  const renderInputType = () => {
    switch (type) {
      case `tel`:
        return <PhoneInput {...dataProps} />
      case `textarea`:
        return <textarea rows="3" {...dataProps} />
      case `select`:
        return <SelectInput {...dataProps} />
      default:
        return <input type={type} {...dataProps} />
    }
  }

  return (
    <div className="form-field">
      <label className={`field-label`}>
        <span>{label}</span>
      </label>
      {renderInputType()}
      {children}
    </div>
  )
}

function Checkbox({ label, children, required, ...restProps }) {
  return (
    <div className="form-field">
      <label className="field-label">
        <input type="checkbox" required={required} {...restProps} />
        <span>
          {label ?? children}
          {required ? <span style={{ color: `red` }}>*</span> : ``}
        </span>
      </label>
    </div>
  )
}

function FieldGroup({ className, children }) {
  return <div className={`field-group ${className}`}>{children}</div>
}

const EnquiryForm = (props) => {
  const {
    formId,
    openButtonText,
    buttonClassName,
    header,
    fields,
    hiddenFields,
    actionUri,
    content,
    submitEvents,
  } = props
  const [open, setOpen] = useState(false)
  const [emailConsentGiven, setEmailConsentGiven] = useState(false)
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false)

  const handleSubmit = (event) => {
    event.preventDefault()
    CheckForUTMvariables()
    submitEvents()
    event.target.submit()
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className={buttonClassName || `button is-primary`}
      >
        {openButtonText}
      </Button>
      <Dialog
        className="enquiry-form-root"
        open={open}
        onClose={() => setOpen(false)}
      >
        <div className={`enquiry-form-content-wrapper`}>
          <div className={`content`}>
            <h3>{header}</h3>
            <p dangerouslySetInnerHTML={{ __html: content.message }} />
            {content.icons.length > 0 ? (
              <div className="icon-content-list">
                {content.icons.map((icon, index) => {
                  return (
                    <div className="icon" key={index}>
                      <img src={icon.image} alt={icon.text} />
                      <h4 dangerouslySetInnerHTML={{ __html: icon.text }} />
                    </div>
                  )
                })}
              </div>
            ) : (
              ``
            )}
          </div>
          <form
            action={actionUri}
            className={`form-wrapper`}
            id={formId}
            onSubmit={handleSubmit}
          >
            {fields.map((field, index) => {
              return (
                <FieldGroup key={index}>
                  {Array.isArray(field) ? (
                    field.map((item) => <Input key={item.name} data={item} />)
                  ) : (
                    <Input key={field.name} data={field} />
                  )}
                </FieldGroup>
              )
            })}
            <FieldGroup className="field-group-checkbox">
              <Checkbox
                id="terms_accepted"
                name="terms_accepted"
                required={true}
                value="yes"
                checked={hasAgreedToTerms}
                onChange={() => setHasAgreedToTerms(!hasAgreedToTerms)}
              >
                I have read and accepted Silverchef’s{` `}
                <a href="/pages/privacy-policy" target="_blank">
                  Privacy Policy
                </a>
                {` `}
                and{` `}
                <a href="/pages/terms-and-conditions" target="_blank">
                  Terms of Conditions
                </a>
                .
              </Checkbox>
            </FieldGroup>
            <FieldGroup className="field-group-checkbox">
              <Checkbox
                id="consent_to_email"
                name={``}
                value="true"
                checked={emailConsentGiven}
                onChange={() => setEmailConsentGiven(!emailConsentGiven)}
              >
                I would like to receive marketing communications from
                SilverChef, including promotions, offers, and updates. I
                understand that I can unsubscribe at any time.
              </Checkbox>
              {/* This input ensures a value is always sent to salesforce with either `true` or 
                `false` which is relied upon by Pardot form handlers. */}
              <input
                type="hidden"
                name="consent_to_email"
                value={emailConsentGiven}
              />
            </FieldGroup>
            <div className={`hidden`}>
              {hiddenFields.map((field) => {
                return (
                  <input
                    key={field.name}
                    type={`hidden`}
                    value={field.value ?? ``}
                    name={field.name}
                    id={field.name}
                    data-field={field.name}
                  />
                )
              })}
            </div>
            <input type={`submit`} disabled={!hasAgreedToTerms} />
          </form>
        </div>
      </Dialog>
    </>
  )
}

export { EnquiryForm }
